import uniq from 'lodash.uniq';
import { useCallback, useEffect, useState } from 'react';

import { getEventFromCache } from '@general/cache/eventCache';
import { getMemberFromCache } from '@general/cache/memberCache';
import { checkIfMembershipWillLapse } from '@general/helpers/event.helpers';
import productTypes from '@general/productTypes';
import useCartFromAPI from '@hooks/apiLegacy/useCartFromAPI';
import useViewer from '@hooks/useViewer';

const useCartMembersWithSpeedBumps = () => {
    const { viewer } = useViewer();
    const { cart } = useCartFromAPI({ viewerId: viewer?.recordId });
    const [cartMembersWithSpeedBumps, setCartMembersWithSpeedBumps] = useState();

    const getDoesMemberHaveBothItemTypes = useCallback(
        memberId => {
            const cartItemsForMember = cart.items.filter(item => item.member_id === memberId);
            const hasEventItems = cartItemsForMember.some(item => item.product_type === productTypes.EVENT);
            const hasMembershipItems = cartItemsForMember.some(item => item.product_type === productTypes.MEMBERSHIP);
            return hasEventItems && hasMembershipItems;
        },
        [cart]
    );

    const getDoesMemberHaveSpeedBump = useCallback(
        async memberId => {
            const cartItemsForMember = cart.items.filter(item => item.member_id === memberId);
            const member = await getMemberFromCache(memberId);
            const events = await Promise.all(
                cartItemsForMember
                    .filter(item => item.product_type === productTypes.EVENT)
                    .map(item => getEventFromCache(item.meta_info.event_code))
            );

            let hasSpeedBump = false;
            events.forEach(event => {
                if (checkIfMembershipWillLapse({ member, event, cart: null })) {
                    if (cart.locale !== 'US') return (hasSpeedBump = false); // Membership is only USD, can't add membership to cart with non-USD event
                    hasSpeedBump = true;
                }
            });

            return hasSpeedBump;
        },
        [cart]
    );

    useEffect(() => {
        if (!cart) {
            return;
        }

        (async () => {
            const memberIdsInCart = uniq(cart.items.map(item => item.member_id));
            const speedBumps = [];
            await memberIdsInCart.filter(getDoesMemberHaveBothItemTypes).reduce(async (promise, memberId) => {
                await promise;
                const hasSpeedBump = await getDoesMemberHaveSpeedBump(memberId);

                if (hasSpeedBump) {
                    const member = await getMemberFromCache(memberId);
                    speedBumps.push(member);
                }
            }, Promise.resolve());

            setCartMembersWithSpeedBumps(speedBumps);
        })();
    }, [cart, getDoesMemberHaveBothItemTypes, getDoesMemberHaveSpeedBump]);

    return { cartMembersWithSpeedBumps };
};

export default useCartMembersWithSpeedBumps;
